import { useState } from 'react';
import { useLocation } from 'wouter';
import { ArrowLeft, User, Mail, Lock, Camera, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';

export default function Profile() {
  const [, setLocation] = useLocation();
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSaveProfile = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await api.put('/user/profile', { name, email });
      await refreshUser();
      setSuccess('资料更新成功');
      setEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.error || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('两次输入的新密码不一致');
      return;
    }

    if (newPassword.length < 6) {
      setError('新密码长度至少6位');
      return;
    }

    setLoading(true);

    try {
      await api.put('/user/password', {
        currentPassword,
        newPassword
      });
      setSuccess('密码修改成功');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.error || '修改失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a2e3e]">
      {/* 头部 */}
      <div className="p-6 flex items-center gap-4">
        <button
          onClick={() => setLocation('/settings')}
          className="text-white/70 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold text-white">个人资料</h1>
      </div>

      <div className="px-6 pb-8">
        {/* 头像 */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#22a9d9] to-[#6c5ce7] flex items-center justify-center">
              <span className="text-white text-4xl font-bold">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <button className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#22a9d9] flex items-center justify-center hover:bg-[#22a9d9]/80 transition-colors">
              <Camera className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* 基本信息 */}
        <div className="glass rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-medium">基本信息</h2>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="text-[#22a9d9] text-sm hover:text-[#22a9d9]/80 transition-colors"
              >
                编辑
              </button>
            )}
          </div>

          <div className="space-y-4">
            {/* 用户名 */}
            <div>
              <label className="text-white/60 text-sm mb-2 block">用户名</label>
              <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
                <User className="w-5 h-5 text-white/40" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!editing}
                  className="flex-1 bg-transparent text-white outline-none disabled:opacity-60"
                />
              </div>
            </div>

            {/* 邮箱 */}
            <div>
              <label className="text-white/60 text-sm mb-2 block">邮箱</label>
              <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
                <Mail className="w-5 h-5 text-white/40" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!editing}
                  className="flex-1 bg-transparent text-white outline-none disabled:opacity-60"
                />
              </div>
            </div>

            {editing && (
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={loading}
                  className="flex-1 h-12 rounded-xl bg-[#22a9d9] text-white font-medium hover:bg-[#22a9d9]/80 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  保存
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setName(user?.name || '');
                    setEmail(user?.email || '');
                  }}
                  className="flex-1 h-12 rounded-xl bg-white/10 text-white font-medium hover:bg-white/20 transition-colors"
                >
                  取消
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 修改密码 */}
        <div className="glass rounded-2xl p-5">
          <h2 className="text-white font-medium mb-4">修改密码</h2>

          <div className="space-y-4">
            {/* 当前密码 */}
            <div>
              <label className="text-white/60 text-sm mb-2 block">当前密码</label>
              <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
                <Lock className="w-5 h-5 text-white/40" />
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="请输入当前密码"
                  className="flex-1 bg-transparent text-white placeholder-white/40 outline-none"
                />
              </div>
            </div>

            {/* 新密码 */}
            <div>
              <label className="text-white/60 text-sm mb-2 block">新密码</label>
              <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
                <Lock className="w-5 h-5 text-white/40" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="至少6位"
                  className="flex-1 bg-transparent text-white placeholder-white/40 outline-none"
                />
              </div>
            </div>

            {/* 确认新密码 */}
            <div>
              <label className="text-white/60 text-sm mb-2 block">确认新密码</label>
              <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
                <Lock className="w-5 h-5 text-white/40" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="再次输入新密码"
                  className="flex-1 bg-transparent text-white placeholder-white/40 outline-none"
                />
              </div>
            </div>

            <button
              onClick={handleChangePassword}
              disabled={loading || !currentPassword || !newPassword || !confirmPassword}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-[#22a9d9] to-[#6c5ce7] text-white font-medium hover:shadow-glow transition-shadow disabled:opacity-50"
            >
              {loading ? '修改中...' : '确认修改'}
            </button>
          </div>
        </div>

        {/* 消息提示 */}
        {error && (
          <div className="mt-4 bg-red-500/20 border border-red-500/50 rounded-2xl p-4 text-red-300 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mt-4 bg-green-500/20 border border-green-500/50 rounded-2xl p-4 text-green-300 text-sm">
            {success}
          </div>
        )}
      </div>
    </div>
  );
}
